import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  LinearProgress,
  Tooltip,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Badge,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Speed as SpeedIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  NotificationsActive as NotificationsActiveIcon,
  Timer as TimerIcon,
  Lock as LockIcon,
  Memory as MemoryIcon,
  Send as SendIcon,
  Search as SearchIcon,
  Check as CheckIcon,
  ReportProblem as AlertIcon
} from '@mui/icons-material';
import api from '../services/api';

const MonitoringDashboard: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<any>(null);
  const [uptimeData, setUptimeData] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshIntervalSec = 15;
  const [countdown, setCountdown] = useState(15);

  // Online users filter & search
  const [searchUser, setSearchUser] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');

  // SSL Checker state
  const [customSslDomain, setCustomSslDomain] = useState('');
  const [checkingSsl, setCheckingSsl] = useState(false);
  const [sslResult, setSslResult] = useState<any>(null);

  // Alert settings state
  const [alertSettings, setAlertSettings] = useState<any>({
    monitoring_alert_email_enabled: '1',
    admin_email: '',
    monitoring_alert_line_enabled: '0',
    monitoring_alert_line_token: '',
    monitoring_cpu_threshold: '90',
    monitoring_ram_threshold: '90',
    monitoring_disk_threshold: '90',
    monitoring_latency_threshold: '2000',
    monitoring_ssl_domain: 'tp.kpru.ac.th'
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);

  // Fetch Dashboard Summary
  const fetchDashboardData = useCallback(async (isSilent = false) => {
    if (!isSilent) setRefreshing(true);
    try {
      const [dashRes, uptimeRes] = await Promise.all([
        api.get('/monitoring/dashboard'),
        api.get('/monitoring/uptime-history?limit=30')
      ]);
      setData(dashRes.data);
      setUptimeData(uptimeRes.data);
      if (dashRes.data?.ssl) {
        setSslResult(dashRes.data.ssl);
      }
    } catch (err) {
      console.error('Error loading monitoring data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setCountdown(refreshIntervalSec);
    }
  }, [refreshIntervalSec]);

  // Fetch Alert Settings
  const fetchAlertSettings = async () => {
    try {
      const res = await api.get('/monitoring/settings');
      setAlertSettings((prev: any) => ({ ...prev, ...res.data }));
      if (res.data.monitoring_ssl_domain) {
        setCustomSslDomain(res.data.monitoring_ssl_domain);
      }
    } catch (err) {
      console.error('Error loading alert settings:', err);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchAlertSettings();
  }, [fetchDashboardData]);

  // Auto-refresh timer & countdown
  useEffect(() => {
    if (!autoRefresh) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchDashboardData(true);
          return refreshIntervalSec;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [autoRefresh, refreshIntervalSec, fetchDashboardData]);

  // Handle Save Alert Settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/monitoring/settings', alertSettings);
      alert('บันทึกการตั้งค่าระบบเฝ้าระวังและการแจ้งเตือนเรียบร้อยแล้ว');
      fetchDashboardData(true);
    } catch (err: any) {
      alert('บันทึกไม่สำเร็จ: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingSettings(false);
    }
  };

  // Handle Test Alert
  const handleTestAlert = async (channel: 'LINE' | 'EMAIL' | 'ALL') => {
    setTestingAlert(true);
    try {
      await api.post('/monitoring/test-alert', {
        channel,
        line_token: alertSettings.monitoring_alert_line_token,
        test_email: alertSettings.admin_email
      });
      alert('ส่งสัญญาณทดสอบเรียบร้อยแล้ว กรุณาตรวจสอบช่องทาง ' + channel);
    } catch (err: any) {
      alert('ทดสอบล้มเหลว: ' + (err.response?.data?.error || err.message));
    } finally {
      setTestingAlert(false);
    }
  };

  // Handle Manual SSL Check
  const handleCheckSsl = async () => {
    setCheckingSsl(true);
    try {
      const res = await api.get(`/monitoring/ssl-status?domain=${encodeURIComponent(customSslDomain || 'tp.kpru.ac.th')}`);
      setSslResult(res.data);
    } catch (err: any) {
      alert('ตรวจสอบ SSL ไม่สำเร็จ: ' + (err.response?.data?.error || err.message));
    } finally {
      setCheckingSsl(false);
    }
  };

  // Handle Resolve Incident
  const handleResolveIncident = async (id: number) => {
    try {
      await api.put(`/monitoring/incidents/${id}/resolve`);
      fetchDashboardData(true);
    } catch (err: any) {
      alert('ไม่สามารถปิดรายการได้: ' + err.message);
    }
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <CircularProgress size={52} />
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>กำลังเชื่อมต่อระบบเฝ้าระวัง (System Monitoring)...</Typography>
      </Box>
    );
  }

  const server = data?.server || {};
  const online = data?.online || { totalOnline: 0, activeNow: 0, users: [], roleBreakdown: {}, safeToDeploy: {} };
  const perf = data?.performance || {};
  const dbHealth = data?.database || { isHealthy: true, latencyMs: 0 };
  const ssl = sslResult || data?.ssl || {};
  const uptime = data?.uptime || {};
  const safeDeploy = online.safeToDeploy || { status: 'SAFE', title: 'ปลอดภัย', message: '' };

  // Filter Online Users
  const filteredUsers = (online.users || []).filter((u: any) => {
    const matchesSearch = !searchUser || 
      (u.fullName && u.fullName.toLowerCase().includes(searchUser.toLowerCase())) ||
      (u.username && u.username.toLowerCase().includes(searchUser.toLowerCase())) ||
      (u.email && u.email.toLowerCase().includes(searchUser.toLowerCase())) ||
      (u.currentPath && u.currentPath.toLowerCase().includes(searchUser.toLowerCase()));
    const matchesRole = roleFilter === 'ALL' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Role labels and color mapper
  const roleLabelMap: Record<string, { label: string; color: any }> = {
    ADMIN: { label: 'ผู้ดูแลระบบ (Admin)', color: 'error' },
    STAFF: { label: 'เจ้าหน้าที่ (Staff)', color: 'secondary' },
    SUPERVISOR: { label: 'อาจารย์นิเทศก์', color: 'primary' },
    CO_TEACHER: { label: 'ครูพี่เลี้ยง', color: 'success' },
    STUDENT: { label: 'นักศึกษา', color: 'info' },
    DEAN: { label: 'คณบดี/ผู้บริหาร', color: 'warning' },
    UNKNOWN: { label: 'ผู้ใช้ทั่วไป', color: 'default' }
  };

  const formatUptimeDuration = (seconds: number) => {
    if (!seconds) return '0 นาที';
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${d > 0 ? `${d} วัน ` : ''}${h > 0 ? `${h} ชม. ` : ''}${m} นาที`;
  };

  return (
    <Box sx={{ pb: 6 }}>
      {/* Top Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SpeedIcon color="primary" sx={{ fontSize: 36 }} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              รายงานสถานะระบบ (System Monitoring)
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            ติดตามผู้ใช้ออนไลน์แบบเรียลไทม์, วัดความเร็วและประสิทธิภาพ, ตรวจสอบ Uptime/Downtime, ทรัพยากรเซิร์ฟเวอร์ และความปลอดภัย SSL
          </Typography>
        </Box>

        {/* Global Action Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip
            icon={dbHealth.isHealthy ? <CheckCircleIcon sx={{ fontSize: '18px !important' }} /> : <ErrorIcon sx={{ fontSize: '18px !important' }} />}
            label={dbHealth.isHealthy ? 'ระบบทำงานปกติ (Operational)' : 'ระบบขัดข้อง (Degraded)'}
            color={dbHealth.isHealthy ? 'success' : 'error'}
            variant="filled"
            sx={{ fontWeight: 'bold' }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {autoRefresh ? `อัปเดตอัตโนมัติ (${countdown}s)` : 'หยุดอัปเดต'}
              </Typography>
            }
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => fetchDashboardData()}
            disabled={refreshing}
            sx={{ borderRadius: 2 }}
          >
            รีเฟรชข้อมูล
          </Button>
        </Box>
      </Box>

      {/* 1. Real-time Safe-to-Deploy Alert Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor:
            safeDeploy.status === 'HIGH_RISK' ? '#fecaca' :
            safeDeploy.status === 'CAUTION' ? '#fed7aa' : '#bbf7d0',
          bgcolor:
            safeDeploy.status === 'HIGH_RISK' ? '#fef2f2' :
            safeDeploy.status === 'CAUTION' ? '#fffbeb' : '#f0fdf4',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {safeDeploy.status === 'HIGH_RISK' ? (
            <AlertIcon sx={{ color: '#dc2626', fontSize: 36, mt: 0.3 }} />
          ) : safeDeploy.status === 'CAUTION' ? (
            <WarningIcon sx={{ color: '#d97706', fontSize: 36, mt: 0.3 }} />
          ) : (
            <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 36, mt: 0.3 }} />
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: safeDeploy.status === 'HIGH_RISK' ? '#991b1b' : (safeDeploy.status === 'CAUTION' ? '#9a3412' : '#166534') }}>
                {safeDeploy.title}
              </Typography>
              <Chip
                label={`ออนไลน์ขณะนี้: ${online.totalOnline} คน`}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  bgcolor: safeDeploy.status === 'HIGH_RISK' ? '#fee2e2' : (safeDeploy.status === 'CAUTION' ? '#ffedd5' : '#dcfce7'),
                  color: safeDeploy.status === 'HIGH_RISK' ? '#991b1b' : (safeDeploy.status === 'CAUTION' ? '#9a3412' : '#166534')
                }}
              />
              {safeDeploy.activeScoringCount > 0 && (
                <Chip
                  label={`กำลังประเมิน/กรอกคะแนน: ${safeDeploy.activeScoringCount} คน`}
                  size="small"
                  color="error"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
              {safeDeploy.message}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignSelf: { xs: 'flex-start', md: 'center' } }}>
          <Button
            size="small"
            variant="contained"
            color={safeDeploy.status === 'HIGH_RISK' ? 'error' : (safeDeploy.status === 'CAUTION' ? 'warning' : 'success')}
            onClick={() => setTabIndex(0)}
            startIcon={<PeopleIcon />}
            sx={{ borderRadius: 2, fontWeight: 'bold' }}
          >
            ดูรายชื่อผู้ใช้ออนไลน์ ({online.totalOnline})
          </Button>
        </Box>
      </Paper>

      {/* Segmented Navigation Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, val) => setTabIndex(val)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<Badge badgeContent={online.totalOnline} color="primary"><PeopleIcon /></Badge>}
            iconPosition="start"
            label="ผู้ใช้ออนไลน์ (Online Users)"
          />
          <Tab icon={<TimerIcon />} iconPosition="start" label="สถานะการทำงาน & Uptime" />
          <Tab icon={<SpeedIcon />} iconPosition="start" label="ความเร็วและประสิทธิภาพ" />
          <Tab icon={<StorageIcon />} iconPosition="start" label="ทรัพยากรเซิร์ฟเวอร์ (Server)" />
          <Tab icon={<SecurityIcon />} iconPosition="start" label="ความปลอดภัยและ SSL" />
          <Tab icon={<NotificationsActiveIcon />} iconPosition="start" label="การแจ้งเตือน (Alerts)" />
        </Tabs>
      </Paper>

      {/* TAB 0: ONLINE USERS */}
      {tabIndex === 0 && (
        <Box>
          {/* Quick Metrics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={1} sx={{ borderRadius: 2, borderLeft: '4px solid #1976d2' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>ผู้ใช้ออนไลน์รวม (15 นาที)</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', mt: 0.5 }}>
                    {online.totalOnline} <span style={{ fontSize: '1rem', fontWeight: 500 }}>คน</span>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    กำลังใช้งานอยู่ขณะนี้: <strong>{online.activeNow}</strong> คน
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={1} sx={{ borderRadius: 2, borderLeft: '4px solid #0288d1' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>นักศึกษา (Students)</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main', mt: 0.5 }}>
                    {online.roleBreakdown?.STUDENT || 0} <span style={{ fontSize: '1rem', fontWeight: 500 }}>คน</span>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ส่งงานและดูแฟ้มสะสมงาน
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={1} sx={{ borderRadius: 2, borderLeft: '4px solid #7b1fa2' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>อาจารย์นิเทศก์ (Supervisors)</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#7b1fa2', mt: 0.5 }}>
                    {online.roleBreakdown?.SUPERVISOR || 0} <span style={{ fontSize: '1rem', fontWeight: 500 }}>คน</span>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    กำลังประเมิน/กรอกคะแนน
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={1} sx={{ borderRadius: 2, borderLeft: '4px solid #2e7d32' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>ครูพี่เลี้ยง & เจ้าหน้าที่</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main', mt: 0.5 }}>
                    {(online.roleBreakdown?.CO_TEACHER || 0) + (online.roleBreakdown?.STAFF || 0) + (online.roleBreakdown?.ADMIN || 0)} <span style={{ fontSize: '1rem', fontWeight: 500 }}>คน</span>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ครูพี่เลี้ยง: {online.roleBreakdown?.CO_TEACHER || 0} | แอดมิน: {online.roleBreakdown?.ADMIN || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search & Filter Toolbar */}
          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="ค้นหาชื่อผู้ใช้, ชื่อ-สกุล, อีเมล หรือหน้าที่กำลังเปิดอยู่..."
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>คัดกรองตามบทบาท (Role)</InputLabel>
                  <Select
                    value={roleFilter}
                    label="คัดกรองตามบทบาท (Role)"
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">ทุกบทบาท (ทั้งหมด)</MenuItem>
                    <MenuItem value="STUDENT">นักศึกษา (STUDENT)</MenuItem>
                    <MenuItem value="SUPERVISOR">อาจารย์นิเทศก์ (SUPERVISOR)</MenuItem>
                    <MenuItem value="CO_TEACHER">ครูพี่เลี้ยง (CO_TEACHER)</MenuItem>
                    <MenuItem value="STAFF">เจ้าหน้าที่ (STAFF)</MenuItem>
                    <MenuItem value="ADMIN">ผู้ดูแลระบบ (ADMIN)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Online Users Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 700, width: 60 }}>สถานะ</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ผู้ใช้งาน (User / Name)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>บทบาท (Role)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>หน้าที่กำลังเปิดอยู่ (Active Path)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>กิจกรรมล่าสุด</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>IP & Browser</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography color="textSecondary" sx={{ fontWeight: 600 }}>
                        {searchUser || roleFilter !== 'ALL' ? 'ไม่พบผู้ใช้ที่ตรงกับเงื่อนไขการค้นหา' : 'ขณะนี้ไม่มีผู้ใช้อื่นออนไลน์อยู่ในระบบ'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u: any, idx: number) => {
                    const roleInfo = roleLabelMap[u.role] || roleLabelMap.UNKNOWN;
                    return (
                      <TableRow key={u.userId || idx} hover>
                        <TableCell align="center">
                          <Tooltip title={u.isCurrentlyActive ? 'ออนไลน์ขณะนี้ (< 5 นาที)' : 'พักการใช้งาน (5-15 นาที)'}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: u.isCurrentlyActive ? '#22c55e' : '#f59e0b',
                                mx: 'auto',
                                boxShadow: u.isCurrentlyActive ? '0 0 8px #22c55e' : 'none'
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {u.fullName || u.username || 'ผู้ใช้งาน'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {u.username} {u.studentCode ? `• รหัส ${u.studentCode}` : ''} {u.majorOrDept ? `• ${u.majorOrDept}` : ''} {u.email ? `(${u.email})` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={roleInfo.label}
                            size="small"
                            color={roleInfo.color}
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: u.isScoring ? 'error.main' : 'text.primary', fontWeight: u.isScoring ? 700 : 400 }}>
                            {u.currentPath || '/dashboard'}
                          </Typography>
                          {u.isScoring && (
                            <Chip label="กำลังประเมิน/ให้คะแนน" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem', mt: 0.3 }} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {u.idleSeconds < 60 ? 'เมื่อสักครู่' : `${Math.floor(u.idleSeconds / 60)} นาทีที่แล้ว`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(u.lastActiveAt).toLocaleTimeString('th-TH')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                            IP: {u.ipAddress || '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', maxWidth: 220, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            {u.userAgent || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* TAB 1: UPTIME & DOWNTIME */}
      {tabIndex === 1 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card elevation={1} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 700 }}>
                    สถานะการทำงานปัจจุบัน (Current Health)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
                    <CheckCircleIcon sx={{ fontSize: 44, color: 'success.main' }} />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: 'success.main' }}>
                        เปิดใช้งานปกติ (100% Up)
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ระบบบริการ Web Server และ Database ทำงานปกติ
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <span>เวลาเริ่มต้นระบบ:</span>
                    <strong>{new Date(data?.serverStartTime || Date.now()).toLocaleString('th-TH')}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>ทำงานต่อเนื่องมาแล้ว (Uptime):</span>
                    <strong>{formatUptimeDuration(server?.processUptimeSeconds)}</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Card elevation={1} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 700 }}>
                    ความพร้อมใช้งาน (Availability SLA)
                  </Typography>
                  <Box sx={{ my: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
                      {uptime?.uptimePercent30d || 99.9}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      บันทึกการตรวจสอบ Uptime ตลอด 30 วันที่ผ่านมา
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uptime?.uptimePercent30d || 99.9}
                    color="primary"
                    sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    ตรวจสอบสถานะทุก 60 วินาที (บันทึกรวม {uptime?.totalChecks || 0} ครั้ง)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Card elevation={1} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 700 }}>
                    ฐานข้อมูล MariaDB
                  </Typography>
                  <Box sx={{ my: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: dbHealth.isHealthy ? 'success.main' : 'error.main' }}>
                      {dbHealth.isHealthy ? 'เชื่อมต่อสมบูรณ์ (Healthy)' : 'ขาดการเชื่อมต่อ (Error)'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      Query Latency: <strong>{dbHealth.latencyMs} ms</strong>
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Active DB Connections:</span>
                    <strong>{server?.database?.activeConnections || 1} Sessions</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <span>Database Engine:</span>
                    <strong>{server?.database?.version || 'MariaDB 11.4'}</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Incident History Table */}
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                ประวัติเหตุการณ์และข้อขัดข้อง (Incidents & Outage Log)
              </Typography>
              <Chip label="ตรวจจับอัตโนมัติ" size="small" variant="outlined" color="primary" />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>ประเภทเหตุการณ์</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>ระดับความรุนแรง</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>รายละเอียด</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>เวลาที่เกิด</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>สถานะ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!uptimeData?.incidents || uptimeData.incidents.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary" sx={{ fontWeight: 600 }}>
                          🎉 ไม่พบประวัติข้อขัดข้องหรือ Downtime ในช่วงที่ผ่านมา ระบบทำงานได้เสถียร 100%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    uptimeData.incidents.map((inc: any) => (
                      <TableRow key={inc.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{inc.incident_type}</TableCell>
                        <TableCell>
                          <Chip
                            label={inc.severity}
                            size="small"
                            color={inc.severity === 'CRITICAL' ? 'error' : (inc.severity === 'WARNING' ? 'warning' : 'info')}
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{inc.title}</Typography>
                          <Typography variant="caption" color="textSecondary">{inc.message}</Typography>
                        </TableCell>
                        <TableCell>{new Date(inc.started_at).toLocaleString('th-TH')}</TableCell>
                        <TableCell align="center">
                          {inc.is_resolved ? (
                            <Chip label="แก้ไขแล้ว" size="small" color="success" variant="outlined" />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleResolveIncident(inc.id)}
                            >
                              ปิดรายการ
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* TAB 2: PERFORMANCE & RESPONSE TIME */}
      {tabIndex === 2 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={1} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>เวลาตอบสนองเฉลี่ย (Avg Latency)</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: perf?.avgLatencyMs < 200 ? 'success.main' : 'warning.main', mt: 0.5 }}>
                    {perf?.avgLatencyMs || 0} <span style={{ fontSize: '1rem', fontWeight: 500 }}>ms</span>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    95th Percentile: <strong>{perf?.p95LatencyMs || 0} ms</strong>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={1} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>คำขอต่อนาที (RPM Throughput)</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', mt: 0.5 }}>
                    {perf?.rpm || 0} <span style={{ fontSize: '1rem', fontWeight: 500 }}>req/min</span>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    คำขอทั้งหมด: {perf?.totalRequests?.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={1} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>MariaDB Query Latency</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: dbHealth.latencyMs < 50 ? 'success.main' : 'warning.main', mt: 0.5 }}>
                    {dbHealth?.latencyMs || 0} <span style={{ fontSize: '1rem', fontWeight: 500 }}>ms</span>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    SELECT 1 Ping Latency
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card elevation={1} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>HTTP 2xx สำเร็จ (Success Rate)</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main', mt: 0.5 }}>
                    {perf?.statusDistribution ? Math.round(((perf.statusDistribution['2xx'] || 1) / Math.max(1, (perf.statusDistribution['2xx'] || 0) + (perf.statusDistribution['4xx'] || 0) + (perf.statusDistribution['5xx'] || 0))) * 100) : 100}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    4xx: {perf?.statusDistribution?.['4xx'] || 0} | 5xx: {perf?.statusDistribution?.['5xx'] || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Slowest Endpoints Table */}
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
              8 อันดับ API ที่ใช้เวลาประมวลผลนานที่สุด (Slowest Endpoints)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Endpoint Path & Method</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>จำนวนคำขอ (Calls)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>เวลาตอบสนองเฉลี่ย (Avg)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>เวลาตอบสนองสูงสุด (Max)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>การประเมิน</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!perf?.slowestEndpoints || perf.slowestEndpoints.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">กำลังรวบรวมข้อมูลสถิติ API...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    perf.slowestEndpoints.map((ep: any, idx: number) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {ep.endpoint}
                        </TableCell>
                        <TableCell align="center">{ep.count}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: ep.avgLatencyMs > 500 ? 'warning.main' : 'text.primary' }}>
                          {ep.avgLatencyMs} ms
                        </TableCell>
                        <TableCell align="center">{ep.maxLatencyMs} ms</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ep.avgLatencyMs < 200 ? 'รวดเร็วมาก' : (ep.avgLatencyMs < 500 ? 'ปกติ' : 'ต้องปรับปรุง')}
                            size="small"
                            color={ep.avgLatencyMs < 200 ? 'success' : (ep.avgLatencyMs < 500 ? 'info' : 'warning')}
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* TAB 3: SERVER & INFRASTRUCTURE */}
      {tabIndex === 3 && (
        <Box>
          <Grid container spacing={3}>
            {/* CPU & RAM */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MemoryIcon color="primary" /> การใช้งาน CPU และ Memory (RAM)
                </Typography>

                {/* CPU Bar */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      CPU Utilization ({server?.cpu?.cores || 1} Cores)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: server?.cpu?.usagePercent > 80 ? 'error.main' : 'primary.main' }}>
                      {server?.cpu?.usagePercent || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={server?.cpu?.usagePercent || 0}
                    color={server?.cpu?.usagePercent > 80 ? 'error' : (server?.cpu?.usagePercent > 60 ? 'warning' : 'primary')}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    CPU Model: {server?.cpu?.model || 'Intel / AMD Processor'}
                  </Typography>
                </Box>

                {/* RAM Bar */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      System Memory (RAM: {server?.memory?.usedGB || 0} / {server?.memory?.totalGB || 0} GB)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: server?.memory?.usagePercent > 85 ? 'error.main' : 'success.main' }}>
                      {server?.memory?.usagePercent || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={server?.memory?.usagePercent || 0}
                    color={server?.memory?.usagePercent > 85 ? 'error' : (server?.memory?.usagePercent > 70 ? 'warning' : 'success')}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    RAM ว่างคงเหลือ: <strong>{server?.memory?.freeGB || 0} GB</strong>
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Node.js Heap */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Node.js Process Memory Heap
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="textSecondary">Heap Used</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800 }}>{server?.memory?.processHeapUsedMB || 0} MB</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="textSecondary">Heap Total</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800 }}>{server?.memory?.processHeapTotalMB || 0} MB</Typography>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Typography variant="caption" color="textSecondary">Resident Set (RSS)</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800 }}>{server?.memory?.processRssMB || 0} MB</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Disk & Platform */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <StorageIcon color="primary" /> พื้นที่จัดเก็บข้อมูล (Disk Space)
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Primary Storage Drive (ใช้งาน {server?.disk?.diskUsedGB || 0} / {server?.disk?.diskTotalGB || 0} GB)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: server?.disk?.diskUsagePercent > 85 ? 'error.main' : 'primary.main' }}>
                      {server?.disk?.diskUsagePercent || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={server?.disk?.diskUsagePercent || 0}
                    color={server?.disk?.diskUsagePercent > 85 ? 'error' : (server?.disk?.diskUsagePercent > 70 ? 'warning' : 'primary')}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                    พื้นที่ว่างคงเหลือ: <strong>{server?.disk?.diskFreeGB || 0} GB</strong> | โฟลเดอร์ Uploads: <strong>{server?.disk?.uploadsSizeMB || 0} MB</strong>
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  ข้อมูลเซิร์ฟเวอร์และระบบปฏิบัติการ
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', py: 0.8 }}>OS Platform / Host:</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>{server?.os?.type} ({server?.os?.hostname})</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', py: 0.8 }}>Node.js Runtime:</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>{server?.nodeVersion} (PID: {server?.pid})</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', py: 0.8 }}>OS Architecture:</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>{server?.os?.arch} ({server?.os?.platform})</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', py: 0.8 }}>OS System Uptime:</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 0.8 }}>{formatUptimeDuration(server?.serverUptimeSeconds)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* TAB 4: SECURITY & SSL */}
      {tabIndex === 4 && (
        <Box>
          <Grid container spacing={3}>
            {/* SSL Certificate Card */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LockIcon color="primary" /> ใบรับรองความปลอดภัย (SSL / TLS Certificate)
                </Typography>

                <Box sx={{ p: 2, bgcolor: ssl.isValid ? '#f0fdf4' : '#fef2f2', borderRadius: 2, border: '1px solid', borderColor: ssl.isValid ? '#bbf7d0' : '#fecaca', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {ssl.isValid ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />}
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        {ssl.isValid ? 'ใบรับรอง SSL ถูกต้องและปลอดภัย' : 'ใบรับรองมีข้อผิดพลาดหรือหมดอายุ'}
                      </Typography>
                    </Box>
                    <Chip
                      label={ssl.status === 'DEV_LOCAL' ? 'Local Dev' : (ssl.daysRemaining > 30 ? `คงเหลือ ${ssl.daysRemaining} วัน` : `เตือน: เหลือ ${ssl.daysRemaining} วัน`)}
                      color={ssl.daysRemaining > 30 ? 'success' : 'warning'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {ssl.message}
                  </Typography>
                </Box>

                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', py: 1 }}>โดเมนเป้าหมาย (Domain):</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}>{ssl.domain || 'tp.kpru.ac.th'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', py: 1 }}>ผู้ออกใบรับรอง (Issuer):</TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}>{ssl.issuer || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', py: 1 }}>วันที่เริ่มต้นใช้งาน (Valid From):</TableCell>
                      <TableCell sx={{ py: 1 }}>{ssl.validFrom ? new Date(ssl.validFrom).toLocaleDateString('th-TH') : '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', py: 1 }}>วันที่หมดอายุ (Valid To):</TableCell>
                      <TableCell sx={{ py: 1, fontWeight: 700, color: ssl.daysRemaining <= 30 ? 'error.main' : 'text.primary' }}>
                        {ssl.validTo ? new Date(ssl.validTo).toLocaleDateString('th-TH') : '-'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Box sx={{ mt: 3, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="ระบุโดเมนที่ต้องการตรวจสอบ..."
                    value={customSslDomain}
                    onChange={(e) => setCustomSslDomain(e.target.value)}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCheckSsl}
                    disabled={checkingSsl}
                    startIcon={checkingSsl ? <CircularProgress size={16} /> : <SecurityIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    ตรวจสอบ SSL
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Security Checklist */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SecurityIcon color="primary" /> สรุปความปลอดภัยระบบ (Security Overview)
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>HTTP Security Headers (Helmet.js)</Typography>
                      <Typography variant="caption" color="textSecondary">ป้องกัน XSS, Clickjacking, MIME sniffing</Typography>
                    </Box>
                    <Chip label="เปิดใช้งาน (Active)" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Rate Limiting & Anti Brute-force</Typography>
                      <Typography variant="caption" color="textSecondary">จำกัดความถี่การยิงคำขอและสุ่มเดารหัสผ่าน</Typography>
                    </Box>
                    <Chip label="เปิดใช้งาน (Active)" color="success" size="small" sx={{ fontWeight: 'bold' }} />
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>ความพยายามล็อกอินล้มเหลว (24 ชม.)</Typography>
                      <Typography variant="caption" color="textSecondary">Failed login attempts tracked by audit logs</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: data?.security?.failed_logins_24h > 10 ? 'error.main' : 'success.main' }}>
                      {data?.security?.failed_logins_24h || 0} ครั้ง
                    </Typography>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>การเข้าสู่ระบบสำเร็จ (24 ชม.)</Typography>
                      <Typography variant="caption" color="textSecondary">Successful logins in the last 24 hours</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                      {data?.security?.successful_logins_24h || 0} ครั้ง
                    </Typography>
                  </Paper>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* TAB 5: ALERTING SYSTEM CONFIG */}
      {tabIndex === 5 && (
        <Box>
          <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 860, mx: 'auto' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <NotificationsActiveIcon color="primary" /> ตั้งค่าระบบแจ้งเตือนฉุกเฉิน (Alerting Channels & Thresholds)
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              กำหนดช่องทางรับการแจ้งเตือนทันทีเมื่อเว็บไซต์ล่ม, ฐานข้อมูลขัดข้อง, หรือทรัพยากรเซิร์ฟเวอร์สูงเกินเกณฑ์
            </Typography>

            {/* Email Notification Channel */}
            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={alertSettings.monitoring_alert_email_enabled === '1'}
                    onChange={(e) => setAlertSettings({ ...alertSettings, monitoring_alert_email_enabled: e.target.checked ? '1' : '0' })}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 700 }}>การแจ้งเตือนผ่าน Email (SMTP Alert)</Typography>}
              />
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                ระบบจะส่งอีเมลแจ้งเตือนไปยังแอดมินโดยอัตโนมัติเมื่อเกิดเหตุการณ์ฉุกเฉิน
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="อีเมลผู้รับการแจ้งเตือน (Admin Email)"
                value={alertSettings.admin_email || ''}
                onChange={(e) => setAlertSettings({ ...alertSettings, admin_email: e.target.value })}
                placeholder="admin@kpru.ac.th"
                disabled={alertSettings.monitoring_alert_email_enabled !== '1'}
                sx={{ bgcolor: 'white' }}
              />
            </Box>

            {/* LINE Notification Channel */}
            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={alertSettings.monitoring_alert_line_enabled === '1'}
                    onChange={(e) => setAlertSettings({ ...alertSettings, monitoring_alert_line_enabled: e.target.checked ? '1' : '0' })}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 700 }}>การแจ้งเตือนผ่าน LINE Notify / LINE Bot Webhook</Typography>}
              />
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                รองรับทั้ง LINE Notify Token หรือ Webhook URL (Discord / Slack / Teams Webhook)
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="LINE Notify Token หรือ Webhook URL"
                value={alertSettings.monitoring_alert_line_token || ''}
                onChange={(e) => setAlertSettings({ ...alertSettings, monitoring_alert_line_token: e.target.value })}
                placeholder="ใส่ Token จาก notify-bot.line.me หรือใส่ Webhook URL"
                disabled={alertSettings.monitoring_alert_line_enabled !== '1'}
                sx={{ bgcolor: 'white' }}
              />
            </Box>

            {/* Thresholds */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              เกณฑ์แจ้งเตือนการใช้งานทรัพยากร (Resource Thresholds)
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="เกณฑ์เตือน CPU Usage (%)"
                  value={alertSettings.monitoring_cpu_threshold || '90'}
                  onChange={(e) => setAlertSettings({ ...alertSettings, monitoring_cpu_threshold: e.target.value })}
                  helperText="แจ้งเตือนเมื่อ CPU ใช้งานเกินกี่เปอร์เซ็นต์ (แนะนำ 85-90%)"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="เกณฑ์เตือน Memory RAM (%)"
                  value={alertSettings.monitoring_ram_threshold || '90'}
                  onChange={(e) => setAlertSettings({ ...alertSettings, monitoring_ram_threshold: e.target.value })}
                  helperText="แจ้งเตือนเมื่อ RAM ใช้งานเกินกี่เปอร์เซ็นต์ (แนะนำ 85-90%)"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="เกณฑ์เตือน Disk Space (%)"
                  value={alertSettings.monitoring_disk_threshold || '90'}
                  onChange={(e) => setAlertSettings({ ...alertSettings, monitoring_disk_threshold: e.target.value })}
                  helperText="แจ้งเตือนเมื่อพื้นที่ดิสก์ถูกใช้งานเกินกี่เปอร์เซ็นต์ (แนะนำ 85-90%)"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="เกณฑ์เตือน API Latency (ms)"
                  value={alertSettings.monitoring_latency_threshold || '2000'}
                  onChange={(e) => setAlertSettings({ ...alertSettings, monitoring_latency_threshold: e.target.value })}
                  helperText="แจ้งเตือนเมื่อ API ตอบสนองช้าเกินกี่มิลลิวินาที (แนะนำ 2000 ms)"
                />
              </Grid>
            </Grid>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleTestAlert('LINE')}
                  disabled={testingAlert || alertSettings.monitoring_alert_line_enabled !== '1'}
                  startIcon={<SendIcon />}
                >
                  ทดสอบส่ง LINE
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => handleTestAlert('EMAIL')}
                  disabled={testingAlert || alertSettings.monitoring_alert_email_enabled !== '1'}
                  startIcon={<SendIcon />}
                >
                  ทดสอบส่ง Email
                </Button>
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveSettings}
                disabled={savingSettings}
                startIcon={savingSettings ? <CircularProgress size={16} /> : <CheckIcon />}
                sx={{ borderRadius: 2, px: 3, fontWeight: 'bold' }}
              >
                บันทึกการตั้งค่า
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MonitoringDashboard;
